import React, { useState, useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, Play, Maximize2 } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Mode galerie immersive pour les "Réalisations".
 * Activé via settings.realisations_display_mode === "gallery".
 *
 * Props :
 *   items : tableau de realisations (chaque item a images[], youtube_url, title, slug, category)
 *   columns : nb de colonnes desktop (2/3/4)
 *   useLightbox : si true, clic ouvre lightbox au lieu de naviguer vers détail
 */
export default function RealisationsGalleryView({ items = [], columns = 3, useLightbox = true }) {
  const [lightbox, setLightbox] = useState(null); // { items, index }

  // Construit la liste plate des médias (image + vidéo) avec backref vers l'item parent
  const media = [];
  items.forEach(it => {
    const imgs = it.images || [];
    imgs.forEach((url, i) => media.push({ type: "image", url, item: it, key: `${it.id}-img-${i}` }));
    if (it.youtube_url) {
      const id = ytId(it.youtube_url);
      if (id) media.push({ type: "video", url: it.youtube_url, ytid: id, item: it, key: `${it.id}-video` });
    }
  });

  const open = useCallback((idx) => {
    if (!useLightbox) return;
    setLightbox({ index: idx });
  }, [useLightbox]);

  const close = () => setLightbox(null);
  const prev = useCallback(() => setLightbox(lb => lb ? { index: (lb.index - 1 + media.length) % media.length } : null), [media.length]);
  const next = useCallback(() => setLightbox(lb => lb ? { index: (lb.index + 1) % media.length } : null), [media.length]);

  // Keyboard navigation
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox, prev, next]);

  if (media.length === 0) {
    return (
      <div className="py-20 text-center text-sm text-[hsl(var(--muted-foreground))]" data-testid="gallery-empty">
        Aucun média à afficher pour le moment.
      </div>
    );
  }

  // Colonnes responsive (Tailwind explicit classes pour purge-safe)
  const colsClass = columns === 2 ? "sm:columns-2 lg:columns-2"
                   : columns === 4 ? "sm:columns-2 lg:columns-3 xl:columns-4"
                                   : "sm:columns-2 lg:columns-3";

  return (
    <>
      <div
        className={`columns-1 ${colsClass} gap-4 md:gap-5 [column-fill:_balance]`}
        data-testid="realisations-gallery-grid"
      >
        {media.map((m, idx) => {
          const Wrapper = useLightbox ? "button" : Link;
          const wrapperProps = useLightbox
            ? { type: "button", onClick: () => open(idx) }
            : { to: `/realisations/${m.item.slug}` };
          return (
            <Wrapper
              key={m.key}
              {...wrapperProps}
              className="group relative break-inside-avoid mb-4 md:mb-5 block w-full overflow-hidden rounded-2xl bg-[color:var(--brand-navy)] shadow-soft hover:shadow-hover transition-all"
              data-testid={`gallery-item-${idx}`}
            >
              {m.type === "image" ? (
                <img
                  src={m.url}
                  alt={m.item.title}
                  loading="lazy"
                  className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-500"
                />
              ) : (
                <YouTubeHoverPreview ytid={m.ytid} title={m.item.title}/>
              )}
              <div className="absolute inset-x-0 bottom-0 p-3 md:p-4 bg-gradient-to-t from-black/80 via-black/30 to-transparent text-white opacity-0 group-hover:opacity-100 transition-opacity">
                {m.item.category && (
                  <span className="text-[10px] uppercase tracking-wider font-bold text-[color:var(--brand-sky)]">{m.item.category}</span>
                )}
                <div className="font-display text-sm md:text-base font-bold leading-tight mt-1 line-clamp-2">{m.item.title}</div>
              </div>
              <Maximize2 className="absolute top-2.5 right-2.5 w-4 h-4 text-white/70 opacity-0 group-hover:opacity-100 transition-opacity" />
            </Wrapper>
          );
        })}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[80] bg-black/95 flex items-center justify-center p-3 md:p-8 animate-fade-up"
          onClick={close}
          data-testid="gallery-lightbox"
        >
          <button
            onClick={(e) => { e.stopPropagation(); close(); }}
            className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur flex items-center justify-center text-white"
            aria-label="Fermer"
            data-testid="gallery-lightbox-close"
          >
            <X className="w-5 h-5" />
          </button>
          {media.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); prev(); }}
                className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur flex items-center justify-center text-white"
                aria-label="Précédent"
                data-testid="gallery-lightbox-prev"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); next(); }}
                className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur flex items-center justify-center text-white"
                aria-label="Suivant"
                data-testid="gallery-lightbox-next"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}
          <div className="relative max-w-6xl w-full max-h-[88vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            {(() => {
              const m = media[lightbox.index];
              if (!m) return null;
              return m.type === "image" ? (
                <img src={m.url} alt={m.item.title} className="max-w-full max-h-[80vh] object-contain rounded-xl shadow-2xl" data-testid="gallery-lightbox-image" />
              ) : (
                <div className="w-full aspect-video max-h-[80vh] rounded-xl overflow-hidden shadow-2xl">
                  <iframe
                    src={`https://www.youtube.com/embed/${m.ytid}?autoplay=1`}
                    title={m.item.title}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="autoplay; encrypted-media; fullscreen"
                    allowFullScreen
                    data-testid="gallery-lightbox-video"
                  />
                </div>
              );
            })()}
            <div className="mt-4 text-white text-center max-w-2xl">
              <div className="font-display text-base md:text-lg font-bold">{media[lightbox.index]?.item.title}</div>
              {media[lightbox.index]?.item.category && (
                <div className="text-xs uppercase tracking-wider text-[color:var(--brand-sky)] mt-1">{media[lightbox.index].item.category}</div>
              )}
              <div className="text-[11px] text-white/50 mt-2">{lightbox.index + 1} / {media.length}</div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ytId(url) {
  if (!url) return "";
  const m = String(url).match(/(?:v=|\.be\/|embed\/|shorts\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : "";
}

/**
 * YouTubeHoverPreview — affiche le thumbnail YouTube en idle, et au hover
 * lance le player muet+autoplay+sans contrôles dans la miniature même.
 * Une fois le mouse out, retour au thumbnail (player détruit pour économiser CPU).
 */
function YouTubeHoverPreview({ ytid, title }) {
  const [hovering, setHovering] = useState(false);
  // Délai pour ne pas charger l'iframe sur survol fugace
  useEffect(() => {
    if (!hovering) return;
    const timer = setTimeout(() => setHovering("active"), 300);
    return () => clearTimeout(timer);
  }, [hovering]);

  return (
    <div
      className="relative aspect-video w-full bg-black overflow-hidden"
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      data-testid="youtube-hover-preview"
    >
      {/* Thumbnail (toujours présent, masqué visuellement quand l'iframe est prête) */}
      <img
        src={`https://img.youtube.com/vi/${ytid}/hqdefault.jpg`}
        alt={title}
        loading="lazy"
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${hovering === "active" ? "opacity-0" : "opacity-90"}`}
      />
      {/* Lecteur YouTube embed muet+autoplay+sans contrôles, seulement quand hovering */}
      {hovering === "active" && (
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${ytid}?autoplay=1&mute=1&controls=0&loop=1&playlist=${ytid}&modestbranding=1&playsinline=1&rel=0&iv_load_policy=3`}
          title={`${title} — preview`}
          className="absolute inset-0 w-full h-full pointer-events-none"
          frameBorder="0"
          allow="autoplay; encrypted-media; picture-in-picture"
          loading="lazy"
        />
      )}
      {/* Play button overlay */}
      <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${hovering === "active" ? "opacity-0" : "opacity-100"}`}>
        <div className="w-14 h-14 rounded-full bg-[color:var(--brand-red,#dc2626)] flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
          <Play className="w-6 h-6 text-white ml-1" fill="currentColor" />
        </div>
      </div>
      {/* Badge VIDÉO en haut à gauche */}
      <span className="absolute top-2.5 left-2.5 px-2 py-0.5 rounded-full bg-black/70 text-[10px] font-bold uppercase tracking-wider text-white inline-flex items-center gap-1 backdrop-blur">
        <Play className="w-2.5 h-2.5" fill="currentColor"/> Vidéo
      </span>
    </div>
  );
}
